import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
} from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { usePageNotice } from '../context/FeedbackContext';
import OtpInput from '../components/OtpInput';
import LanguageSelector from '../components/inputs/LanguageSelector';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60;

function maskEmail(email) {
  if (!email) return 'your email';
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const masked =
    local.length <= 2 ? local : `${local.slice(0, 2)}${'*'.repeat(Math.max(1, local.length - 3))}${local.slice(-1)}`;
  return `${masked}@${domain}`;
}

function formatCountdown(seconds) {
  const m = String(Math.floor(seconds / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
}

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [mfaToken, setMfaToken] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN);
  const [resendEnabled, setResendEnabled] = useState(false);

  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  usePageNotice('login-error', error ? { message: error, severity: 'error', persist: true } : null);
  const mfaStep = !!mfaToken;
  const mfaCode = digits.join('');
  const hasError = !!error && mfaStep;

  useEffect(() => {
    if (!mfaStep) return;
    setCountdown(RESEND_COOLDOWN);
    setResendEnabled(false);
  }, [mfaStep]);

  useEffect(() => {
    if (!mfaStep || resendEnabled) return;
    if (countdown <= 0) { setResendEnabled(true); return; }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [mfaStep, countdown, resendEnabled]);

  const resetResend = useCallback(() => {
    setCountdown(RESEND_COOLDOWN);
    setResendEnabled(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    if (mfaStep) {
      const result = await verifyMfa(mfaToken, mfaCode);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || t('auth.verifying'));
        setDigits(Array(OTP_LENGTH).fill(''));
      }
    } else {
      const result = await login(email, password);
      if (result.success) {
        navigate('/dashboard');
      } else if (result.requiresMfa) {
        setMfaToken(result.mfaToken);
        setDigits(Array(OTP_LENGTH).fill(''));
        setError('');
      } else {
        setError(result.error);
      }
    }
    setLoading(false);
  };

  const handleResendCode = async () => {
    if (!resendEnabled || resendLoading) return;
    setError('');
    setResendLoading(true);
    try {
      await axios.post('/api/auth/mfa/resend', { mfaToken });
      setDigits(Array(OTP_LENGTH).fill(''));
      resetResend();
    } catch (err) {
      setError(err.response?.data?.error || t('auth.resendFailed'));
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaToken(null);
    setDigits(Array(OTP_LENGTH).fill(''));
    setError('');
  };

  // ── MFA step ─────────────────────────────────────────────────────────────────
  if (mfaStep) {
    return (
      <Container component="main" maxWidth="xs" sx={{ px: 2 }}>
        <Box
          sx={{
            marginTop: { xs: '32px', sm: '64px' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, width: '100%', textAlign: 'center' }}>
            <Typography component="h1" variant="h4" align="center" gutterBottom>
              Pich Jewels
            </Typography>

            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t('auth.verificationCode')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('auth.enterCodeSentTo')}
            </Typography>
            <Typography variant="body2" fontWeight={500} color="text.primary">
              {maskEmail(email)}
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <OtpInput digits={digits} onChange={(d) => { setDigits(d); setError(''); }} hasError={hasError} />

              {hasError && (
                <Typography variant="body2" color="error" sx={{ mb: 1.5, fontWeight: 500 }}>
                  {error}
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: 'monospace', fontSize: '1rem' }}>
                {formatCountdown(countdown)}
              </Typography>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || mfaCode.length !== OTP_LENGTH}
                sx={{ mb: 1 }}
              >
                {loading ? t('auth.verifying') : hasError ? t('auth.tryAgain') : t('auth.verify')}
              </Button>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('auth.didntReceiveCode')}{' '}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  fontWeight={600}
                  onClick={handleResendCode}
                  disabled={!resendEnabled || resendLoading}
                  sx={{
                    color: resendEnabled && !resendLoading ? 'primary.main' : 'text.disabled',
                    cursor: resendEnabled && !resendLoading ? 'pointer' : 'default',
                    textDecoration: resendEnabled && !resendLoading ? 'underline' : 'none',
                  }}
                >
                  {resendLoading ? t('auth.sendingCode') : t('auth.resendCode')}
                  {!resendEnabled && !resendLoading ? ` (${formatCountdown(countdown)})` : ''}
                </Link>
              </Typography>

              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  color="text.secondary"
                  onClick={handleBackToLogin}
                >
                  {t('auth.backToSignIn')}
                </Link>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // ── Login step ────────────────────────────────────────────────────────────────
  return (
    <Container component="main" maxWidth="xs" sx={{ px: 2 }}>
      <Box
        sx={{
          marginTop: { xs: '32px', sm: '64px' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <LanguageSelector size="small" />
          </Box>

          <Typography component="h1" variant="h4" align="center" gutterBottom>
          Pich Jewels
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            {t('auth.appSubtitle')}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('auth.email')}
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('auth.password')}
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 1 }}
              disabled={loading}
            >
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Link component={RouterLink} to="/forgot-password" variant="body2">
                {t('auth.forgotPassword')}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
